import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

import { useStyles } from '~/assets/styles';
import useClaimFlagConfigurations from '~/components/ClaimFlags/hooks/useClaimFlagConfigurations';
import useClaimStageConfigurations from '~/components/ClaimStages/hooks/useClaimStageConfigurations';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import TextFieldFormik from '~/components/TextFieldFormik';

import { getLobDescription } from '../../../../../Utils/getLobDescription';
import useOrganization from '../../../../OrganizationContext';
import copies from '../copies.json';
import FieldConfigurationCard from '../FieldConfigurationCard/FieldConfigurationCard';
import ShowRequiredPanel from '../ShowRequiredPanel/ShowRequiredPanel';

import BaseConfigurationSection from './BaseConfigurationSection';

const ID_PREFIX = 'configuration.fnol';

const GeneralDetailsSection = () => {
  const { classes } = useStyles();
  const { values, setFieldValue, initialValues } = useFormikContext();
  const { lobConfigurationsDict } = useLobConfiguration();
  const { subOrganizationEnabled, subOrganizations } = useOrganization();
  const { isOperational: isClaimStageOperational } = useClaimStageConfigurations();
  const { isOperational: isClaimFlagsOperationalForOrg } = useClaimFlagConfigurations();
  const instructionsExist = get(initialValues, `${ID_PREFIX}.instructions.desc`, undefined) !== undefined;
  const stageKeyExists = get(initialValues, `${ID_PREFIX}.stage_key`, undefined) !== undefined;
  const claimFlagsExists = get(initialValues, `${ID_PREFIX}.claim_flags`, undefined) !== undefined;

  // Backfill the instructions default for older configs. Not gated on async data, so it settles on mount.
  useEffect(() => {
    if (!instructionsExist) {
      setFieldValue(`${ID_PREFIX}.instructions.desc`, copies.default_instructions);
      setFieldValue(`${ID_PREFIX}.instructions.active`, true);
    }
  }, [instructionsExist, setFieldValue]);

  // stage_key / claim_flags are offered only once their configs resolve (async), and OrganizationContext
  // renders these children before that fetch completes. Backfilling reactively — not mount-only — means a
  // cold load still seeds the field once the operational flag flips true; otherwise the field never gets
  // seeded and saving the config later fails an opaque payload validation. Each field has its own effect so
  // a re-run when one flag resolves can't re-seed (and clobber) the other.
  useEffect(() => {
    if (isClaimStageOperational && !stageKeyExists) {
      setFieldValue(`${ID_PREFIX}.stage_key`, {
        desc: 'Claim Stage',
        type: 'select',
        active: false,
        mandatory: false,
        options: [],
        dynamic: true,
      });
    }
  }, [isClaimStageOperational, stageKeyExists, setFieldValue]);

  useEffect(() => {
    if (isClaimFlagsOperationalForOrg && !claimFlagsExists) {
      setFieldValue(`${ID_PREFIX}.claim_flags`, {
        desc: 'Claim Flags',
        type: 'multiselect',
        active: false,
        mandatory: false,
        options: [],
        dynamic: true,
      });
    }
  }, [isClaimFlagsOperationalForOrg, claimFlagsExists, setFieldValue]);

  return (
    <BaseConfigurationSection title="General Details" subtitle={<div className="p-12" />} isSection>
      <Grid item xs={12}>
        <TextFieldFormik id="display_name" label="Name" fullWidth />
      </Grid>
      {subOrganizationEnabled && (
        <Grid item xs={6}>
          <TextFieldFormik id="sub_organization_id" label="Sub Organization" select disabled fullWidth>
            <MenuItem value={values?.sub_organization_id}>
              {subOrganizations.find((so) => so.id === values?.sub_organization_id)?.name}
            </MenuItem>
          </TextFieldFormik>
        </Grid>
      )}
      <Grid item xs={subOrganizationEnabled ? 6 : 12}>
        <TextFieldFormik id="lob" label="Line of Business" select disabled fullWidth>
          <MenuItem value={values?.lob}>{getLobDescription(values?.lob, lobConfigurationsDict)}</MenuItem>
        </TextFieldFormik>
      </Grid>
      <Grid item xs={12}>
        <TextFieldFormik
          id={`${ID_PREFIX}.instructions.desc`}
          label="Instructions"
          fullWidth
          multiline
          rows={2}
          className={classes.multilineWithoutScroll}
        />
      </Grid>
      <ShowRequiredPanel
        id={`${ID_PREFIX}.fnol_policyholder_contact_id`}
        label={copies.policyholder_details_verification}
      />
      {isClaimStageOperational ? <ShowRequiredPanel id={`${ID_PREFIX}.stage_key`} label={copies.claim_stage} /> : null}
      {isClaimFlagsOperationalForOrg ? (
        <ShowRequiredPanel id={`${ID_PREFIX}.claim_flags`} label={copies.claim_flags} />
      ) : null}
      <FieldConfigurationCard idPrefix={ID_PREFIX} fieldId="submission_mode" forceRequired />
      <FieldConfigurationCard idPrefix={ID_PREFIX} fieldId="reported_date" forceRequired />
    </BaseConfigurationSection>
  );
};

export default GeneralDetailsSection;
